

import { Link, useNavigate } from "react-router-dom";
import React, { useState } from "react";
import tokenService from "../../services/token-service";
import apiUrls from "../../config/api-urls";
import { apiRequest } from "../../services/api-client";
import { useAuth } from "../../context/auth-context";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Toaster } from "react-hot-toast";
import { X, Eye, EyeOff } from "lucide-react";
import Logo from "../../assets/images/logo/hazer-logo.png";

const LoginComponent = () => {
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuth();

  const validationSchema = Yup.object({
    phoneNumber: Yup.string()
      .matches(/^[0-9]{10,14}$/, "Invalid phone number")
      .required("Phone number is required"),
    password: Yup.string()
      .min(6, "Password must be at least 6 characters")
      .required("Password is required"),
  });

  const formik = useFormik({
    initialValues: {
      phoneNumber: "",
      password: "",
    },
    validationSchema,
    onSubmit: async (values) => {
      setLoading(true);

      const formattedValues = {
        ...values,
        phoneNumber: values.phoneNumber.startsWith("0")
          ? values.phoneNumber
          : `${values.phoneNumber}`,
      };

      try {
        const response = await apiRequest(
          "POST",
          apiUrls.auth.login,
          formattedValues
        );

        const user = response?.data?.data?.user;

        if (user?.token) {
          login(user.token, user);

          const roleName = (user?.activeRole || user?.role?.name)?.toLowerCase();
          let redirectPath = "/";

          if (roleName === "handyman") {
            redirectPath = user?.handyman?.isSubmitted
              ? "/handyman/tasks"
              : "/handyman-onboarding";
          } else if (roleName === "customer") {
            redirectPath = "/customer/dashboard";
          } else if (
            roleName === "candidate" ||
            roleName === "marketing_executive" ||
            roleName === "marketing executive"
          ) {
            redirectPath = "/dashboard";
          }

          setTimeout(() => navigate(redirectPath), 1500);
        }
      } catch (error) {
        console.error("Login error:", error);
      } finally {
        setLoading(false);
      }
    },
  });

  return (
    <div className="w-full min-h-screen flex items-center justify-center relative p-4">
      <Toaster position="top-right" reverseOrder={false} />

      <div className="w-full max-w-5xl bg-white rounded-lg shadow-lg overflow-hidden relative">
        {/* Close Button */}
        <button
          onClick={() => navigate(-1)}
          className="absolute top-4 right-4 text-black p-2 rounded-full hover:bg-[#ff9906] hover:text-white transition"
        >
          <X className="h-6 w-6" />
        </button>

        <div className="flex flex-col sm:flex-row">
          {/* Left Section */}
          <div className="hidden sm:flex sm:w-1/2 flex-col items-center justify-center text-center p-6 bg-gradient-to-br from-[#ff9906] to-[#f79d2d] text-white">
            <h2 className="text-2xl md:text-3xl font-bold mb-2">
              Welcome to Hazer Globals
            </h2>
            <p className="text-sm md:text-lg">
              We make it easy for everyone to access their account.
            </p>

            <div className="bg-white rounded-2xl mt-4">
              <img
                src={Logo || "/placeholder.svg"}
                alt="Building"
                className="mt-6 w-32 h-32 md:w-40 md:h-40 max-w-xs rounded-lg shadow-lg"
              />
            </div>
          </div>

          {/* Right Section */}
          <div className="w-full sm:w-1/2 p-6">
            <div className="flex justify-center sm:justify-between items-center mb-4">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-800 text-center">
                Login
              </h2>
            </div>

            <form className="mt-4" onSubmit={formik.handleSubmit}>
              {/* Phone */}
              <div className="mb-4">
                <input
                  type="text"
                  name="phoneNumber"
                  value={formik.values.phoneNumber}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  placeholder="Phone Number"
                  className="w-full border border-gray-300 p-3 text-sm focus:outline-none focus:border-[#ff9906]"
                />
                {formik.touched.phoneNumber &&
                  formik.errors.phoneNumber && (
                    <p className="text-red-500 text-xs mt-1">
                      {formik.errors.phoneNumber}
                    </p>
                  )}
              </div>

              {/* Password with Eye */}
              <div className="mb-4 relative">
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  value={formik.values.password}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  placeholder="Password"
                  className="w-full border border-gray-300 p-3 pr-12 text-sm focus:outline-none focus:border-[#ff9906]"
                />

                {/* Eye Button */}
                <button
                  type="button"
                  onClick={() => setShowPassword((prev) => !prev)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-[#ff9906]"
                  tabIndex={-1}
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5" />
                  ) : (
                    <Eye className="h-5 w-5" />
                  )}
                </button>

                {formik.touched.password && formik.errors.password && (
                  <p className="text-red-500 text-xs mt-1">
                    {formik.errors.password}
                  </p>
                )}
              </div>

              {/* Remember + Forgot */}
              <div className="mb-4 flex flex-col sm:flex-row items-center justify-between text-sm">
                <label className="flex items-center text-gray-600 mb-2 sm:mb-0">
                  <input type="checkbox" className="mr-2 h-4 w-4" />
                  Remember me
                </label>

                <Link
                  to="/forgot-password"
                  className="text-[#ff9906] hover:underline"
                >
                  Forgot Password?
                </Link>
              </div>

              {/* Buttons */}
              <div className="flex flex-col md:flex-row space-y-4 md:space-y-0 md:space-x-4">
                <button
                  type="submit"
                  className="w-full md:w-1/2 rounded bg-[#ff9906] py-2 text-white text-center"
                >
                  {loading ? (
                    <span className="animate-pulse">Logging in...</span>
                  ) : (
                    "Login"
                  )}
                </button>

                <Link
                  to="/register"
                  className="w-full md:w-1/2 rounded border border-[#ff9906] py-2 text-gray-600 hover:bg-[#ff9906] hover:text-white text-center"
                >
                  Register
                </Link>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginComponent;
